import {create} from 'zustand';
import {orderApi} from '../services/api/orderApi';

export const useOrderStore = create((set, get) => ({
  orders: [],
  currentOrder: null,
  isLoading: false,
  filters: {},
  pagination: {page: 0, size: 20, totalElements: 0, totalPages: 0},
  searchResults: [],

  fetchOrders: async (params = {}) => {
    set({isLoading: true});
    try {
      const {filters} = get();
      const response = await orderApi.list({
        page: params.page || 0,
        size: params.size || 20,
        ...filters,
        ...params,
      });
      const data = response.data.data;
      set({
        orders: data.content,
        pagination: {
          page: data.page,
          size: data.size,
          totalElements: data.totalElements,
          totalPages: data.totalPages,
        },
      });
    } finally {
      set({isLoading: false});
    }
  },

  fetchOrderDetail: async id => {
    set({isLoading: true});
    try {
      const response = await orderApi.getById(id);
      set({currentOrder: response.data.data});
    } finally {
      set({isLoading: false});
    }
  },

  createOrder: async data => {
    const response = await orderApi.create(data);
    const newOrder = response.data.data;
    set(state => ({orders: [newOrder, ...state.orders]}));
    return newOrder;
  },

  addLineToOrder: async (orderId, lineData) => {
    const response = await orderApi.addLine(orderId, lineData);
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  updateLine: async (orderId, lineId, lineData) => {
    const response = await orderApi.updateLine(orderId, lineId, lineData);
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  deleteLine: async (orderId, lineId) => {
    const response = await orderApi.deleteLine(orderId, lineId);
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  assignWorkerToInstance: async (orderId, instanceId, workerId) => {
    const response = await orderApi.assignWorkerToInstance(orderId, instanceId, {workerId});
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  updateInstanceStatus: async (orderId, instanceId, status, notes) => {
    const response = await orderApi.updateInstanceStatus(orderId, instanceId, {status, notes});
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  updateOrderStatus: async (id, status, notes) => {
    const response = await orderApi.updateStatus(id, {status, notes});
    const updated = response.data.data;
    set(state => ({
      orders: state.orders.map(o => (o.id === id ? {...o, status} : o)),
      currentOrder:
        state.currentOrder?.id === id ? updated : state.currentOrder,
    }));
  },

  updateOrderDate: async (id, orderDate) => {
    const response = await orderApi.updateOrderDate(id, {orderDate});
    const updated = response.data.data;
    set(state => ({
      orders: state.orders.map(o => (o.id === id ? {...o, orderDate} : o)),
      currentOrder:
        state.currentOrder?.id === id ? updated : state.currentOrder,
    }));
  },

  uploadLineImages: async (orderId, lineId, clothImages, patternImages) => {
    const formData = new FormData();
    if (clothImages) {
      clothImages.forEach((img, i) => {
        formData.append('clothImages', {
          uri: img.uri,
          type: img.type || 'image/jpeg',
          name: img.fileName || `cloth_${i}.jpg`,
        });
      });
    }
    if (patternImages) {
      patternImages.forEach((img, i) => {
        formData.append('patternImages', {
          uri: img.uri,
          type: img.type || 'image/jpeg',
          name: img.fileName || `pattern_${i}.jpg`,
        });
      });
    }
    const response = await orderApi.uploadLineImages(orderId, lineId, formData);
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  uploadLineAudio: async (orderId, lineId, audioFile) => {
    const formData = new FormData();
    formData.append('audio', {
      uri: audioFile.uri,
      type: audioFile.type || 'audio/mpeg',
      name: audioFile.fileName || 'audio.mp3',
    });
    const response = await orderApi.uploadLineAudio(orderId, lineId, formData);
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  deleteLineAudio: async (orderId, lineId, audioIndex) => {
    const response = await orderApi.deleteLineAudio(orderId, lineId, audioIndex);
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  updateLineAudioNote: async (orderId, lineId, audioIndex, note) => {
    const response = await orderApi.updateLineAudioNote(orderId, lineId, audioIndex, note);
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  assignWorker: async (orderId, lineId, workerId) => {
    await orderApi.assignWorkerToLine(orderId, lineId, {workerId});
    await get().fetchOrderDetail(orderId);
  },

  setFilters: filters => {
    set(state => ({filters: {...state.filters, ...filters}}));
  },

  applyDiscount: async (orderId, data) => {
    const response = await orderApi.applyDiscount(orderId, data);
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  removeDiscount: async orderId => {
    const response = await orderApi.removeDiscount(orderId);
    const updated = response.data.data;
    set({currentOrder: updated});
    return updated;
  },

  searchOrders: async params => {
    set({isLoading: true});
    try {
      const response = await orderApi.search(params);
      const data = response.data.data;
      set({searchResults: data.content || []});
      return data.content || [];
    } finally {
      set({isLoading: false});
    }
  },

  clearFilters: () => set({filters: {}}),

  clearCurrentOrder: () => set({currentOrder: null}),
}));
